import { useMemo } from "react";
import type { ComponentType } from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormRegisterReturn,
  UseFormSetValue,
} from "react-hook-form";
import { useRecoilValue } from "recoil";

import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import TextAreaHookForm from "../../../../components/TextAreaHookForm";
import TextInputLabelPhone from "../../../../components/TextInputLabelPhone";
import SelectHookFormMultiDA from "components/SelectHookFormMulti_DA";

import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { FIELDPERMISSIONS } from "../../../PermissionsConstants";

import type {
  DigitalAssetFormValues,
  OfficeOption,
  SelectOption,
} from "../types";

interface DigitalAssetTextAreaProps {
  className?: string;
  areaType?: boolean;
  label?: string;
  preIcon?: any;
  register?: UseFormRegisterReturn;
  validation?: any;
  hasPermission?: boolean;
  secondDiv?: string;
  disabled?: boolean;
  maxLength?: number;
  name?: string;
}

const DigitalAssetTextArea =
  TextAreaHookForm as unknown as ComponentType<DigitalAssetTextAreaProps>;

interface DigitalAssetFormFieldsProps {
  isEdit: boolean;
  lockOffice: boolean;
  offices: OfficeOption[];
  services: SelectOption[];
  onlineDirectories: SelectOption[];
  appleCategories: SelectOption[];
  appleAppNames: SelectOption[];
  isLoadingOptions: boolean;
  control: Control<DigitalAssetFormValues>;
  register: UseFormRegister<DigitalAssetFormValues>;
  setValue: UseFormSetValue<DigitalAssetFormValues>;
  errors: FieldErrors<DigitalAssetFormValues>;
}

const DigitalAssetFormFields = ({
  isEdit,
  lockOffice,
  offices,
  services,
  onlineDirectories,
  appleCategories,
  appleAppNames,
  isLoadingOptions,
  control,
  register,
  setValue,
  errors,
}: DigitalAssetFormFieldsProps) => {
  const userPermissions = useRecoilValue(permissionsSelector);

  const viewFacebook =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_facebook;
  const viewDirectoryGeneralDescription =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_directorygeneraldescription;
  const viewYelpProfileUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_yelpprofileurl;
  const viewYelpDescription =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_yelpdescription;
  const viewGoogleBusinessUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_googlebusinessurl;
  const viewGoogleDescription =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_googledescription;
  const viewTwitterUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_twitterurl;
  const viewLinkedInUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_linkedinurl;
  const viewYouTubeUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_youtubeurl;
  const viewInstagramUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_instagramurl;

  const daOfficeNameOpt = useMemo(
    () =>
      offices.map((office) => ({
        snumber: office.storeNumber,
        agency: office.agency ?? office.brandMarketingName ?? "",
        ostatus: office.officeStatus,
        id: office.officeName,
        officeId: office.officeId,
        value: office.officeCode,
        label: `${office.officeName} - ${office.officeCode}`,
      })),
    [offices],
  );

  const allDigitalService = useMemo(
    () => [{ label: "ALL", value: "ALL" }, ...services],
    [services],
  );

  return (
    <div className="bg-bgDB">
      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Digital Assets Detail
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 px-4 gap-12">
          <div className="space-y-1">
            {!isEdit && (
              <TextInputLabelHookForm
                secondDiv="grid grid-cols-1"
                name="name"
                label="Name"
                type="text"
                placeholder=""
                register={register("name", {})}
                disabled
              />
            )}

            {isEdit ? (
              <TextInputLabelHookForm
                secondDiv="grid grid-cols-1"
                name="officeName"
                label="Office Name"
                type="text"
                placeholder="Office Code"
                register={register("officeName", {})}
                disabled
              />
            ) : (
              <Controller
                control={control}
                name="officeName"
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    secondDiv="grid grid-cols-1"
                    field={field}
                    onChange={(e: any) => {
                      if (lockOffice) return;

                      field.onChange(e?.id);
                      setValue("officeCode", e?.value ?? "");
                      setValue("storeNumber", e?.snumber ?? "");
                      setValue("agency", e?.agency ?? "");
                      setValue("officeStatus", e?.ostatus ?? "");
                      setValue("officeId", e?.officeId ?? 0);
                      setValue(
                        "name",
                        "DA-" + String(e?.snumber ?? "").padStart(5, "0"),
                      );
                    }}
                    value={
                      daOfficeNameOpt.find(
                        (option) => option.id === field.value,
                      )?.value ?? null
                    }
                    options={daOfficeNameOpt}
                    isLoading={isLoadingOptions}
                    isDisabled={lockOffice}
                    label="Office"
                    className={
                      errors.officeName ? "border-2 border-red-500" : ""
                    }
                    required
                    validation={{
                      required: {
                        value: true,
                        message: "Required Field",
                      },
                    }}
                  />
                )}
              />
            )}

            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="officeCode"
              label="Office Code"
              type="text"
              placeholder="Office Code"
              register={register("officeCode", {})}
              disabled
            />

            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="storeNumber"
              label="Corp Office #"
              type="text"
              placeholder=""
              register={register("storeNumber", {})}
              disabled
            />

            <Controller
              control={control}
              name="online_Directories_Brand_Name__C"
              render={({ field }) => (
                <SelectHookFormSingle
                  secondDiv="grid grid-cols-1"
                  field={field}
                  onChange={(e: any) => field.onChange(e?.value ?? null)}
                  value={field.value}
                  options={onlineDirectories}
                  label="Online Directories Brand Name"
                />
              )}
            />

            <Controller
              control={control}
              name="serviceNames"
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormMultiDA
                  onChange={(e: any) => {
                    const selectedValues =
                      e?.map((item: any) => item.value) ?? [];
                    const previousValues = field.value ?? [];
                    const isAllSelectedNow = selectedValues.includes("ALL");
                    const wasAllSelectedBefore = previousValues.includes("ALL");

                    if (isAllSelectedNow && !wasAllSelectedBefore) {
                      field.onChange(
                        allDigitalService.map((option) => option.value),
                      );
                      return;
                    }

                    if (!isAllSelectedNow && wasAllSelectedBefore) {
                      field.onChange([]);
                      return;
                    }

                    field.onChange(
                      selectedValues.filter((value: string) => value !== "ALL"),
                    );
                  }}
                  value={(field.value ?? []).map((value) => ({
                    label: value,
                    value,
                  }))}
                  options={allDigitalService}
                  isLoading={isLoadingOptions}
                  label="Services"
                  className={
                    errors.serviceNames
                      ? "border-2 border-red-500 col-span-2"
                      : ""
                  }
                  isRequired={false}
                  secondDiv="grid grid-cols-1"
                />
              )}
            />
          </div>

          <div className="space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="agency"
              label="Agency"
              type="text"
              placeholder=""
              register={register("agency", {})}
              disabled
            />

            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="officeStatus"
              label="Office Status"
              type="text"
              placeholder=""
              register={register("officeStatus", {})}
              disabled
            />

            <TextInputLabelPhone
              secondDiv="grid grid-cols-1 py-1"
              name="digital_Tracking_Number__C"
              label="Digital Tracking Number"
              pattern="(\(\d{3}\)) \d{3}[-]\d{4}"
              register={register("digital_Tracking_Number__C", {})}
              maxLength={11}
              onChange={(e: any) => {
                const x = e.target.value
                  .replace(/\D/g, "")
                  .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

                if (!x) return;

                e.target.value = !x[2]
                  ? x[1]
                  : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
              }}
            />

            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="shortDescription"
              label="Short Description"
              type="text"
              placeholder="Short Description"
              register={register("shortDescription", {})}
              disabled={false}
            />
          </div>
        </div>
      </div>

      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">Title</div>

        <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 gap-12">
          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="page_Title__C"
              label="Page title"
              type="text"
              placeholder=""
              register={register("page_Title__C", {})}
              maxLength={40}
            />
          </div>

          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="page_Title_Spa__C"
              label="Page title SPA"
              type="text"
              placeholder=""
              register={register("page_Title_Spa__C", {})}
              maxLength={40}
            />
          </div>
        </div>
      </div>

      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Website
        </div>

        <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 gap-12">
          <div className="space-y-1 w-full">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="website__C"
              label="Website"
              type="text"
              placeholder=""
              register={register("website__C", {})}
              maxLength={255}
            />

            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="website_Local_Profile_Url__C"
              label="Website Local Profile URL"
              type="text"
              maxLength={2048}
              placeholder=""
              register={register("website_Local_Profile_Url__C", {})}
            />

            <DigitalAssetTextArea
              secondDiv="grid grid-cols-1"
              name="page_Description__C"
              label="Page Description"
              register={register("page_Description__C", {})}
              maxLength={3000}
            />
          </div>

          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="website_Local_Profile_Url_Spa__C"
              label="Website Local Profile URL SPA"
              type="text"
              maxLength={2048}
              placeholder=""
              register={register("website_Local_Profile_Url_Spa__C", {})}
            />

            <DigitalAssetTextArea
              secondDiv="grid grid-cols-1"
              name="page_Description_Spa__C"
              label="Page Description SPA"
              register={register("page_Description_Spa__C", {})}
              maxLength={3000}
            />
          </div>
        </div>
      </div>

      {(userPermissions?.includes(viewGoogleBusinessUrl) ||
        userPermissions?.includes(viewGoogleDescription)) && (
        <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
          <div className="text-quinary font-bold font-sans py-2 px-4">
            Google
          </div>

          <div className="grid grid-flow-row auto-cols-fr md:grid-cols-2 px-4 gap-x-12 gap-y-2">
            {userPermissions?.includes(viewGoogleBusinessUrl) && (
              <div>
                <TextInputLabelHookForm
                  secondDiv="grid grid-cols-1"
                  name="google_Business_Url__C"
                  label="Google Business URL"
                  type="text"
                  maxLength={2048}
                  placeholder=""
                  register={register("google_Business_Url__C", {})}
                />
              </div>
            )}

            {userPermissions?.includes(viewGoogleDescription) && (
              <div>
                <DigitalAssetTextArea
                  secondDiv="grid grid-cols-1"
                  name="google_Description__C"
                  label="Google Description"
                  maxLength={3000}
                  register={register("google_Description__C", {})}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {(userPermissions?.includes(viewYelpProfileUrl) ||
        userPermissions?.includes(viewYelpDescription)) && (
        <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
          <div className="text-quinary font-bold font-sans py-2 px-4">Yelp</div>

          <div className="grid grid-flow-row auto-cols-fr md:grid-cols-2 px-4 gap-x-12 gap-y-2">
            {userPermissions?.includes(viewYelpProfileUrl) && (
              <div>
                <TextInputLabelHookForm
                  secondDiv="grid grid-cols-1"
                  name="yelp_Profile_Url__C"
                  label="Yelp Profile URL"
                  type="text"
                  maxLength={2048}
                  placeholder=""
                  register={register("yelp_Profile_Url__C", {})}
                />
              </div>
            )}

            {userPermissions?.includes(viewYelpDescription) && (
              <div>
                <DigitalAssetTextArea
                  secondDiv="grid grid-cols-1"
                  name="yelp_Description_Specialties__C"
                  label="Yelp Description (Specialties)"
                  maxLength={3000}
                  register={register("yelp_Description_Specialties__C", {})}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Meta Information
        </div>

        <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 gap-12">
          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="title_Tag__C"
              label="Title Tag"
              type="text"
              placeholder=""
              register={register("title_Tag__C", {})}
              maxLength={70}
            />

            <DigitalAssetTextArea
              secondDiv="grid grid-cols-1"
              name="meta_Description__C"
              label="Meta Description"
              register={register("meta_Description__C", {})}
              maxLength={3000}
            />
          </div>

          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="title_Tag_Spa__C"
              label="Title Tag SPA"
              type="text"
              placeholder=""
              register={register("title_Tag_Spa__C", {})}
              maxLength={70}
            />

            <DigitalAssetTextArea
              secondDiv="grid grid-cols-1"
              name="meta_Description_Spa__C"
              label="Meta Description SPA"
              maxLength={3000}
              register={register("meta_Description_Spa__C", {})}
            />
          </div>
        </div>
      </div>

      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Facebook
        </div>

        <div className="grid grid-flow-row auto-cols-fr md:grid-cols-2 px-4 gap-x-12 gap-y-2">
          {userPermissions?.includes(viewFacebook) && (
            <div>
              <TextInputLabelHookForm
                secondDiv="grid grid-cols-1"
                name="facebook_Username_Page_Url__C"
                label="Facebook Username - Page URL"
                type="text"
                maxLength={2048}
                placeholder=""
                register={register("facebook_Username_Page_Url__C", {})}
              />
            </div>
          )}

          {userPermissions?.includes(viewDirectoryGeneralDescription) && (
            <div>
              <DigitalAssetTextArea
                secondDiv="grid grid-cols-1"
                name="directory_General_Description__C"
                label="Directory General Description"
                maxLength={3000}
                register={register("directory_General_Description__C", {})}
              />
            </div>
          )}

          <DigitalAssetTextArea
            secondDiv="grid grid-cols-1"
            name="directory_Company_Story__C"
            label="Directory Company Story"
            maxLength={1000}
            register={register("directory_Company_Story__C", {})}
          />

          <DigitalAssetTextArea
            secondDiv="grid grid-cols-1"
            name="facebook_Company_Overview__C"
            label="Company Overview"
            maxLength={3000}
            register={register("facebook_Company_Overview__C", {})}
          />
        </div>
      </div>

      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">Bing</div>

        <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 gap-12">
          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="bing"
              label="Bing URL"
              type="text"
              maxLength={3000}
              placeholder=""
              register={register("bing", {})}
            />
          </div>

          <div className="w-full space-y-1">
            <DigitalAssetTextArea
              secondDiv="grid grid-cols-1"
              name="bingDescription"
              label="Bing Description"
              maxLength={3000}
              register={register("bingDescription", {})}
            />
          </div>
        </div>
      </div>

      <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
        <div className="text-quinary font-bold font-sans py-2 px-4">Apple</div>

        <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 gap-12">
          <div className="w-full space-y-1">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="appleURL"
              label="Apple URL"
              type="text"
              maxLength={2048}
              placeholder=""
              register={register("appleURL", {})}
            />
          </div>

          <div className="w-full space-y-1">
            <DigitalAssetTextArea
              secondDiv="grid grid-cols-1"
              name="appleDescription"
              label="Apple Description"
              maxLength={3000}
              register={register("appleDescription", {})}
            />
          </div>
        </div>

        <div className="text-quinary font-bold text-xs font-sans pt-3 pb-2 px-4">
          Action Links
        </div>

        <div className="grid justify-items-start grid-cols-1 md:grid-cols-2 px-4 gap-x-12 gap-y-2">
          <div className="w-full">
            <div className="mb-1">
              <Controller
                control={control}
                name="appleCategoryId"
                render={({ field }) => (
                  <SelectHookFormSingle
                    secondDiv="grid grid-cols-1 gap-y-1"
                    field={field}
                    onChange={(e: any) =>
                      field.onChange(e?.value ? Number(e.value) : null)
                    }
                    value={
                      field.value !== null && field.value !== undefined
                        ? String(field.value)
                        : ""
                    }
                    options={appleCategories}
                    isLoading={isLoadingOptions}
                    label="Category"
                  />
                )}
              />
            </div>
          </div>

          <div className="w-full">
            <TextInputLabelHookForm
              secondDiv="grid grid-cols-1"
              name="appleQuickLink"
              label="Quick Link"
              type="text"
              maxLength={2048}
              placeholder="https://www.example.com"
              register={register("appleQuickLink", {})}
            />
          </div>

          <div className="w-full">
            <div className="mb-1">
              <Controller
                control={control}
                name="appleAppNameId"
                render={({ field }) => (
                  <SelectHookFormSingle
                    secondDiv="grid grid-cols-1 gap-y-1"
                    field={field}
                    onChange={(e: any) =>
                      field.onChange(e?.value ? Number(e.value) : null)
                    }
                    value={
                      field.value !== null && field.value !== undefined
                        ? String(field.value)
                        : ""
                    }
                    options={appleAppNames}
                    isLoading={isLoadingOptions}
                    label="App Name"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {(userPermissions?.includes(viewTwitterUrl) ||
        userPermissions?.includes(viewLinkedInUrl) ||
        userPermissions?.includes(viewYouTubeUrl) ||
        userPermissions?.includes(viewInstagramUrl)) && (
        <div className="mb-2 bg-white box-shadow rounded-lg pb-3">
          <div className="text-quinary font-bold font-sans py-2 px-4">
            Social Network URLs
          </div>

          <div className="grid grid-flow-row auto-cols-fr md:grid-cols-2 px-4 gap-x-12 gap-y-2">
            {userPermissions?.includes(viewTwitterUrl) && (
              <div>
                <TextInputLabelHookForm
                  secondDiv="grid grid-cols-1"
                  name="twitterUrl"
                  label="Twitter URL"
                  type="text"
                  maxLength={2048}
                  placeholder=""
                  register={register("twitterUrl", {})}
                />
              </div>
            )}

            {userPermissions?.includes(viewLinkedInUrl) && (
              <div>
                <TextInputLabelHookForm
                  secondDiv="grid grid-cols-1"
                  name="linkedInUrl"
                  label="LinkedIn URL"
                  type="text"
                  maxLength={2048}
                  placeholder=""
                  register={register("linkedInUrl", {})}
                />
              </div>
            )}

            {userPermissions?.includes(viewYouTubeUrl) && (
              <div>
                <TextInputLabelHookForm
                  secondDiv="grid grid-cols-1"
                  name="youtubeUrl"
                  label="Youtube URL"
                  type="text"
                  maxLength={2048}
                  placeholder=""
                  register={register("youtubeUrl", {})}
                />
              </div>
            )}

            {userPermissions?.includes(viewInstagramUrl) && (
              <div>
                <TextInputLabelHookForm
                  secondDiv="grid grid-cols-1"
                  name="instagramUrl"
                  label="Instagram URL"
                  type="text"
                  maxLength={2048}
                  placeholder=""
                  register={register("instagramUrl", {})}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalAssetFormFields;
